import { PAYLOAD_URL } from "~/constants/all";
import { logError, logInfo } from "~/lib/logger";
import {
  clearNativeToken,
  getNativeToken,
  setNativeToken,
} from "~/lib/nativeToken";
import rest from "~/lib/payload/rest";
import type {
  ApiResponse,
  CaughtError,
  LoginResponse,
  Organization,
  PayloadApiResponse,
  PayloadValidationFieldError,
  UserBase,
  UserOperationResponse,
  UserRole,
} from "~/types";
import { toLowercase } from "~/util/formatting";

/**
 * Interface for the user data returned from login/refresh
 */
interface UserData {
  id: string;
  token: string;
  exp: number;
  user: UserBase;
}

/**
 * Interface for the user data returned from create
 */
interface CreateUserData {
  doc: {
    id: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    verified: boolean;
    role: UserRole;
    organization?: string | Organization;
    createdAt: string;
    updatedAt: string;
  };
  token?: string;
  exp?: number;
  user?: UserBase;
}

/**
 * Interface for forgot password response
 */
interface ForgotPasswordData {
  message: string;
}

/**
 * Interface for reset password response
 */
interface ResetPasswordData {
  token: string;
  user: UserBase;
}

/**
 * Creates a new user
 * @param email - The user's email
 * @param password - The user's password
 * @param firstName - The user's first name
 * @param lastName - The user's last name
 * @param emailToken - Email verification token
 * @param recaptchaToken - Recaptcha token for spam protection
 * @param landlord - Whether the user is a landlord or not
 * @param role - Optional role for the user (e.g., "beta-user")
 * @returns A promise that resolves to the API response, including field-specific validation errors
 */
export const createUser = async ({
  email,
  password,
  firstName,
  lastName,
  emailToken,
  recaptchaToken,
  landlord,
  role,
}: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  emailToken?: string | null | undefined;
  recaptchaToken?: string | null | undefined;
  landlord?: boolean;
  role?: string;
}): Promise<
  | ApiResponse<CreateUserData>
  | (PayloadValidationFieldError & { success: false })
> => {
  const cleanEmail = toLowercase(email);

  try {
    logInfo("payloadApiUsers", "Creating user", {
      email: cleanEmail,
      function: "createUser",
    });

    const data = await rest<PayloadApiResponse<CreateUserData>>(
      `${PAYLOAD_URL}/api/users`,
      {
        email: cleanEmail,
        password,
        firstName,
        lastName,
        token: emailToken,
        recaptchaToken,
        landlord,
        role,
      },
      {},
      false
    );

    if (data.errors) {
      logError("payloadApiUsers", "Failed to create user", {
        email: cleanEmail,
        error: data.errors,
        function: "createUser",
      });

      // Handle nested validation errors (e.g., duplicate email)
      // Structure: data.errors[0].data.errors[0] = { path: string, message: string }
      const errorData = data.errors[0].data;
      if (errorData && !Array.isArray(errorData) && "errors" in errorData) {
        const nestedErrors = (
          errorData as { errors?: Array<{ path: string; message: string }> }
        ).errors;
        if (nestedErrors?.[0]) {
          const fieldError = nestedErrors[0];
          return {
            success: false,
            field: fieldError.path,
            message: fieldError.message,
          };
        }
      }

      // Handle legacy structure: data.errors[0].data[0] = { field: string, message: string }
      if (
        data.errors[0].name === "ValidationError" &&
        data.errors[0].data?.[0]
      ) {
        const fieldError = data.errors[0].data[0];
        return {
          success: false,
          field: fieldError.field,
          message: fieldError.message,
        };
      }

      return {
        success: false,
        message: data.errors[0].message,
      };
    }

    if (!data?.doc?.id) {
      logError("payloadApiUsers", "Failed to create user", {
        email: cleanEmail,
        error: data,
        function: "createUser",
      });

      return {
        success: false,
        message: "Failed to create user",
      };
    }

    return {
      success: true,
      data: { doc: data.doc },
    };
  } catch (e) {
    const error = e as CaughtError;
    logError("payloadApiUsers", "Failed to create user", {
      email: cleanEmail,
      error: error.message,
      function: "createUser",
    });

    return {
      success: false,
      message: error.message,
    };
  }
};

/**
 * Logs in a user
 * @param email - The user's email
 * @param password - The user's password
 * @param token - Email verification token
 * @returns A promise that resolves to the API response
 */
export const loginUser = async (
  email: string,
  password: string,
  token?: string | null | undefined
): Promise<ApiResponse<UserData>> => {
  const cleanEmail = toLowercase(email);

  if (!cleanEmail || !password) {
    logError("payloadApiUsers", "Email and password are required", {
      email: cleanEmail,
      function: "loginUser",
    });

    return {
      success: false,
      message: "Email and password are required",
    };
  }

  try {
    logInfo("payloadApiUsers", "Logging in user", {
      email: cleanEmail,
      function: "loginUser",
    });

    const data = await rest<PayloadApiResponse<LoginResponse>>(
      `${PAYLOAD_URL}/api/users/login`,
      {
        email: cleanEmail,
        password,
        token,
      },
      {},
      false
    );

    if (data?.token) {
      logInfo("payloadApiUsers", "User logged in", {
        email: cleanEmail,
        function: "loginUser",
      });

      // Persist token for native session persistence
      setNativeToken(data.token);

      return {
        success: true,
        data: {
          id: data.user.id,
          token: data.token,
          exp: data.exp ?? 0,
          user: data.user,
        },
      };
    }

    logError("payloadApiUsers", "Failed to login user", {
      email: cleanEmail,
      error: data?.errors || "Something went wrong",
      function: "loginUser",
    });

    return {
      success: false,
      message:
        data?.errors?.[0]?.message || "Something went wrong. Please try again.",
    };
  } catch (e) {
    const error = e as CaughtError;
    logError("payloadApiUsers", "Failed to login user", {
      email: cleanEmail,
      error: Array.isArray(error?.errors)
        ? error.errors[0]?.message
        : error?.errors?.message || error.message,
      function: "loginUser",
    });

    return {
      success: false,
      message: Array.isArray(error?.errors)
        ? error.errors[0]?.message
        : (error?.errors as { message?: string } | undefined)?.message ||
          error.message,
    };
  }
};

/**
 * Interface for the me endpoint response
 */
interface MeData {
  user: UserBase | null;
  token?: string;
  exp?: number;
}

export const getLoggedInUser = async (): Promise<ApiResponse<MeData>> => {
  try {
    logInfo("payloadApiUsers", "Getting logged in user", {
      function: "getLoggedInUser",
    });

    const data = await rest<PayloadApiResponse<MeData>>(
      `${PAYLOAD_URL}/api/users/me?depth=3`,
      undefined,
      {
        method: "GET",
      }
    );

    if (data.errors) {
      logError("payloadApiUsers", "Failed to get logged in user", {
        error: data.errors,
        function: "getLoggedInUser",
      });

      return {
        success: false,
        message: data.errors[0].message,
      };
    }

    return {
      success: true,
      data,
    };
  } catch (e) {
    const error = e as CaughtError;
    logError("payloadApiUsers", "Failed to get logged in user", {
      error: error.message,
      function: "getLoggedInUser",
    });

    return {
      success: false,
      message: error.message,
    };
  }
};

/**
 * Interface for logout response
 */
interface LogoutData {
  message: string;
}

/**
 * Logs out the current user
 * @returns A promise that resolves to the API response
 */
export const logoutUser = (): Promise<LogoutData> => {
  logInfo("payloadApiUsers", "Logging out user", { function: "logoutUser" });

  clearNativeToken();

  return rest<LogoutData>(`${PAYLOAD_URL}/api/users/logout`, {}, {}, false);
};

/**
 * Sends a forgot password email
 * @param email - The user's email
 * @returns A promise that resolves to the API response
 */
export const forgotPassword = async (
  email: string
): Promise<ApiResponse<ForgotPasswordData>> => {
  const cleanEmail = toLowercase(email);

  if (!cleanEmail) {
    logError("payloadApiUsers", "Email is required", {
      email: cleanEmail,
      function: "forgotPassword",
    });

    return {
      success: false,
      message: "Email is required",
    };
  }

  try {
    logInfo("payloadApiUsers", "Forgot password", {
      email: cleanEmail,
      function: "forgotPassword",
    });

    const data = await rest<PayloadApiResponse<UserOperationResponse>>(
      `${PAYLOAD_URL}/api/users/forgot-password`,
      {
        email: cleanEmail,
      },
      {},
      false
    );

    if (data.errors) {
      logError("payloadApiUsers", "Failed to send forgot password email", {
        email: cleanEmail,
        error: data.errors,
        function: "forgotPassword",
      });

      return {
        success: false,
        message: data.errors[0].message,
      };
    }

    return {
      success: true,
      data: {
        message: data.message || "Password reset email sent successfully",
      },
    };
  } catch (e) {
    const error = e as CaughtError;
    logError("payloadApiUsers", "Failed to send forgot password email", {
      email: cleanEmail,
      error: error.message,
      function: "forgotPassword",
    });

    return {
      success: false,
      message: error.message,
    };
  }
};

/**
 * Resets the user's password
 * @param token - The password reset token
 * @param password - The new password
 * @returns A promise that resolves to the API response
 */
export const resetPassword = async (
  token: string,
  password: string
): Promise<ApiResponse<ResetPasswordData>> => {
  if (!token || !password) {
    logError("payloadApiUsers", "Token and password are required", {
      token,
      function: "resetPassword",
    });

    return {
      success: false,
      message: "Token and password are required",
    };
  }

  try {
    logInfo("payloadApiUsers", "Reset password", {
      token,
      function: "resetPassword",
    });

    const data = await rest<PayloadApiResponse<UserOperationResponse>>(
      `${PAYLOAD_URL}/api/users/reset-password`,
      {
        token,
        password,
      },
      {},
      false
    );

    if (data.errors) {
      logError("payloadApiUsers", "Failed to reset password", {
        token,
        error: data.errors,
        function: "resetPassword",
      });

      return {
        success: false,
        message: data.errors[0].message,
      };
    }

    if (!data.token || !data.user) {
      return {
        success: false,
        message: "Invalid response from server",
      };
    }

    return {
      success: true,
      data: {
        token: data.token,
        user: data.user,
      },
    };
  } catch (e) {
    const error = e as CaughtError;
    logError("payloadApiUsers", "Failed to reset password", {
      token,
      error: error.message,
      function: "resetPassword",
    });

    return {
      success: false,
      message: error.message,
    };
  }
};

/**
 * Refreshes the user's token
 * @returns A promise that resolves to the API response
 */
export const refreshToken = async (): Promise<ApiResponse<UserData>> => {
  try {
    logInfo("payloadApiUsers", "Refreshing token", {
      function: "refreshToken",
    });

    const nativeToken = getNativeToken();
    const headers: Record<string, string> = nativeToken
      ? { Authorization: `Bearer ${nativeToken}` }
      : {};

    const res = await fetch(`${PAYLOAD_URL}/api/users/refresh-token`, {
      method: "POST",
      credentials: "include",
      headers,
    });

    const data: UserOperationResponse = await res.json();

    if (data.errors) {
      logError("payloadApiUsers", "Failed to refresh token", {
        error: data.errors,
        function: "refreshToken",
      });

      return {
        success: false,
        message: data.errors[0].message,
      };
    }

    if (!data.refreshedToken || !data.user) {
      return {
        success: false,
        message: "Invalid response from server",
      };
    }

    // Persist the refreshed token for native session persistence
    setNativeToken(data.refreshedToken);

    return {
      success: true,
      data: {
        id: data.user.id,
        token: data.refreshedToken,
        exp: data.exp || 0,
        user: data.user,
      },
    };
  } catch (e) {
    const error = e as CaughtError;
    logError("payloadApiUsers", "Failed to refresh token", {
      error: error.message,
      function: "refreshToken",
    });

    return {
      success: false,
      message: error.message,
    };
  }
};

/**
 * Verifies the logged in user's email
 * @param token - The email verification token
 * @returns A promise that resolves to the API response
 */
export const verifyUserEmail = async (
  token: string
): Promise<ApiResponse<void>> => {
  if (!token) {
    logError("payloadApiUsers", "Token is required", {
      function: "verifyUserEmail",
    });

    return {
      success: false,
      message: "Token is required",
    };
  }

  try {
    logInfo("payloadApiUsers", "Verifying user email", {
      token,
      function: "verifyUserEmail",
    });

    const data = await rest<PayloadApiResponse<UserOperationResponse>>(
      `${PAYLOAD_URL}/api/users/verify-email`,
      { token },
      {},
      false
    );

    if (data.errors) {
      logError("payloadApiUsers", "Failed to verify user email", {
        token,
        error: data.errors,
        function: "verifyUserEmail",
      });

      return {
        success: false,
        message: data.errors[0].message,
      };
    }

    return {
      success: true,
      data: undefined,
    };
  } catch (e) {
    const error = e as CaughtError;
    logError("payloadApiUsers", "Failed to verify user email", {
      token,
      error: error.message,
      function: "verifyUserEmail",
    });

    return {
      success: false,
      message: error.message,
    };
  }
};

/**
 * Resends the user's email verification
 * @returns A promise that resolves to the API response
 */
export const resendUserEmailVerification = async (): Promise<
  ApiResponse<void>
> => {
  try {
    logInfo("payloadApiUsers", "Resend user email verification email", {
      function: "resendUserEmailVerification",
    });

    const data = await rest<PayloadApiResponse<UserOperationResponse>>(
      `${PAYLOAD_URL}/api/users/resend-verification-email`,
      {},
      {},
      false
    );

    if (data.errors) {
      logError("payloadApiUsers", "Failed to send verification email", {
        error: data.errors,
        function: "resendUserEmailVerification",
      });

      return {
        success: false,
        message: data.errors[0].message,
      };
    }

    return {
      success: true,
      data: undefined,
    };
  } catch (e) {
    const error = e as CaughtError;
    logError("payloadApiUsers", "Failed to send verification email", {
      error: error.message,
      function: "resendUserEmailVerification",
    });

    return {
      success: false,
      message: error.message,
    };
  }
};
